import type { HubConnection } from "@microsoft/signalr";

export type IncomingCall = {
  ringId: string;
  customer: string;
  ticketNumber: string;
  rootEquipment: string;
  callerName?: string | null;
  expiresInSeconds: number;
};

type IncomingCallConnectionOptions = {
  url: string;
  getToken: () => Promise<string>;
  onIncomingCall: (call: IncomingCall) => void;
  onEnded: (ringId: string) => void;
};

// The server lists a connection as online for 150 s after each heartbeat.
export const HEARTBEAT_MS = 30_000;
const RETRY_MS = 30_000;

/**
 * Keeps a web app reachable for call invites until `stop()`, retrying forever: nothing else
 * depends on this connection, so while it's down the person just can't be rung here.
 * SignalR loads on demand to stay out of the bundles of apps that don't otherwise use it.
 */
export function startIncomingCallConnection({
  url,
  getToken,
  onIncomingCall,
  onEnded,
}: IncomingCallConnectionOptions) {
  let stopped = false;
  let connection: HubConnection | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  const start = async () => {
    try {
      if (!connection) {
        const signalR = await import("@microsoft/signalr");
        if (stopped) return;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(url, { accessTokenFactory: getToken })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: () => RETRY_MS,
          })
          .configureLogging(signalR.LogLevel.None)
          .build();
        connection.on("IncomingCall", onIncomingCall);
        connection.on("IncomingCallEnded", onEnded);
      }
      await connection.start();
      if (stopped) void connection.stop().catch(() => {});
    } catch {
      if (!stopped) retryTimer = setTimeout(() => void start(), RETRY_MS);
    }
  };

  const heartbeat = setInterval(() => {
    if (connection?.state === "Connected")
      void connection.invoke("HeartbeatAsync").catch(() => {});
  }, HEARTBEAT_MS);

  void start();

  return {
    respond: (ringId: string, accepted: boolean) => {
      void connection?.invoke("RespondAsync", ringId, accepted).catch(() => {});
    },
    stop: () => {
      stopped = true;
      clearTimeout(retryTimer);
      clearInterval(heartbeat);
      void connection?.stop().catch(() => {});
    },
  };
}
